import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Card, Form, Input, Rate, App } from "antd";
import { css } from "@emotion/css";
import {
  CloseOutlined,
  LoadingOutlined,
  SendOutlined,
} from "@ant-design/icons";
import { useDispatch } from "react-redux";

import { useAppSelector } from "hooks/useSelector";
import { useCreateNps, useIgnoreNps } from "queries";
import { setApp } from "redux/configSlice";
import FavIcon from "assets/images/favicon.png";

const Character = ({ index, value }) => {
  return (
    <div
      className={`w-[26px] h-[26px] flex justify-center items-center text-base text-center rounded-[50%] ${
        value >= index + 1
          ? "bg-primary text-white"
          : "bg-[rgb(228,233,240)] text-gray-900"
      }`}
    >
      <span className="text-[13px]">{index}</span>
    </div>
  );
};

const NPS = () => {
  const formRef = useRef();
  const { t } = useTranslation();
  const { message } = App.useApp();
  const dispatch = useDispatch();
  const app = useAppSelector();
  const showNps = app?.showNps;
  const createNps = useCreateNps();
  const ignoreNps = useIgnoreNps();
  const { isLoading: loadingNps } = createNps;
  const { isLoading: loadingIgnore } = ignoreNps;

  const [sent, setSent] = useState(false);
  const [showReview, setShowReview] = useState(false);

  const sendNpsHandler = useCallback(
    (values) => {
      createNps.mutate(
        { ...values, score: values?.score ? values?.score - 1 : 0 },
        {
          onSuccess: (res) => {
            if (res.data.success) {
              setSent(true);
              if (values?.score - 1 >= 9) {
                setShowReview(true);
              }
            }
          },
          onError: () => {
            setSent(false);
            message.error(t("nps.cannot_send_nps"));
          },
        }
      );
    },
    [t, createNps, message]
  );

  const ignoreNpsHandler = useCallback(() => {
    if (sent) {
      setSent(false);
      dispatch(setApp({ showNps: 0 }));
      return;
    }
    if (loadingIgnore) return;
    ignoreNps.mutate();
  }, [ignoreNps, sent, loadingIgnore, dispatch]);

  const reviewGoogleHandler = useCallback(() => {
    const width = 600;
    const height = 700;
    const left = (window?.screen?.width - width) / 2;
    const top = (window?.screen?.height - height) / 2;

    window.open(
      "https://g.page/r/Cb7mm3EjkTg7EBM/review",
      "GajiHub Review",
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes`
    );

    ignoreNpsHandler();
  }, [ignoreNpsHandler]);

  if (!showNps && !sent) {
    return null;
  }

  return (
    <Card
      className={`fixed z-[12] w-[90vw] max-w-[420px] transition-all duration-[ease] delay-[0.5s] left-4 bottom-4 ${css`
        .ant-card-head-title {
          flex: 1 1 !important;
          padding: 16px 0 !important;
          white-space: normal !important;
        }
      `}`}
      title={t(
        sent
          ? "nps.thank_you_for_your_feedback"
          : "nps.how_likely_you_recommend"
      )}
      styles={{
        header: {
          backgroundColor: "var(--primary-color)",
          color: "white",
          fontWeight: "bold",
        },
        body: sent
          ? {
              padding: 0,
            }
          : {},
      }}
    >
      <Button
        type="primary"
        shape="circle"
        className="absolute right-2 top-2"
        icon={loadingIgnore ? <LoadingOutlined /> : <CloseOutlined />}
        onClick={ignoreNpsHandler}
      />
      {!sent && (
        <>
          <Form
            ref={formRef}
            layout="vertical"
            name="nps_form"
            initialValues={{
              score: 0,
              message: "",
            }}
            onFinish={sendNpsHandler}
          >
            <Form.Item name="score">
              <Rate count={11} character={Character} />
            </Form.Item>
            <div className="flex flex-row justify-between mt-2 mb-5">
              <span className="text-[13px]">
                <b>0</b>
                {` - ${t("nps.not_likely")}`}
              </span>
              <span className="text-[13px]">
                <b>10</b>
                {` - ${t("nps.very_likely")}`}
              </span>
            </div>
            <Form.Item
              noStyle
              shouldUpdate={(prev, next) => prev.score !== next.score}
            >
              {({ getFieldValue }) => (
                <>
                  {!!getFieldValue("score") && (
                    <>
                      <Form.Item
                        name="message"
                        label={t("nps.please_mention_another")}
                      >
                        <Input.TextArea autoSize={{ minRows: 3, maxRows: 3 }} />
                      </Form.Item>
                      <Form.Item noStyle>
                        <Button
                          htmlType="submit"
                          type="primary"
                          icon={<SendOutlined />}
                          className="float-right"
                          loading={loadingNps}
                        >
                          {t("button.send")}
                        </Button>
                      </Form.Item>
                    </>
                  )}
                </>
              )}
            </Form.Item>
          </Form>
        </>
      )}
      {!!sent && !!showReview && (
        <div className="flex flex-row items-center justify-center p-5">
          <Button
            className="p-5 border-2"
            icon={<img width={20} src={FavIcon} alt="Logo" />}
            onClick={reviewGoogleHandler}
          >
            <span className="text-primary font-bold">
              {t("nps.give_a_review_of_gajihub")}
            </span>
          </Button>
        </div>
      )}
    </Card>
  );
};

export default NPS;
